import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy, useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AppShell } from "@/components/layout/AppShell";
import { ExamStructureAPI } from "@/lib/exam-structure-api";
import { SyllabusAPI } from "@/lib/syllabus-api";
import { InstitutionsAPI, CampusesAPI, ClassesAPI } from "@/lib/sprint2-api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { TYPE_LABELS, colorOf } from "@/lib/exam-tile-tokens";
import { AssessmentNodeDialog } from "@/components/exam/AssessmentNodeDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  ClipboardCheck, Plus, Save, Trash2, GripVertical, Loader2, PenLine, FileText,
} from "lucide-react";
import { toast } from "sonner";

const EDIT_ROLES = ["super_admin", "inst_admin", "principal", "campus_admin", "hod", "coordinator", "it_admin"];

export default function ExamStructure() {
  const { user } = useAuth();
  const canEdit = (user?.roles || []).some((r) => EDIT_ROLES.includes(r));
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [classFilter, setClassFilter] = useState("all"); // "all" | "templates" | classId

  const instQ = useQuery({ queryKey: ["es-insts"], queryFn: () => InstitutionsAPI.list() });
  const listQ = useQuery({ queryKey: ["es-list"], queryFn: () => ExamStructureAPI.list() });
  const boardsQ = useQuery({ queryKey: ["es-boards"], queryFn: () => SyllabusAPI.boards() });
  // Load classes for the user's institution so we can label & filter plans by class.
  const primaryInstId = user?.institution_id || instQ.data?.[0]?.id;
  const classesQ = useQuery({
    queryKey: ["es-classes", primaryInstId],
    queryFn: () => ClassesAPI.list({ institution_id: primaryInstId }),
    enabled: !!primaryInstId,
  });
  const classMap = useMemo(() => {
    const m = {};
    (classesQ.data || []).forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [classesQ.data]);

  const structures = listQ.data?.structures || [];
  const filteredStructures = useMemo(() => {
    if (classFilter === "all") return structures;
    if (classFilter === "templates") return structures.filter((s) => !s.class_id);
    return structures.filter((s) => s.class_id === classFilter);
  }, [structures, classFilter]);

  const selected = structures.find((s) => s.id === selectedId);

  return (
    <AppShell>
      <div className="p-6 max-w-7xl mx-auto" data-testid="exam-structure-page">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold tracking-tight text-3xl flex items-center gap-2.5">
              <ClipboardCheck className="h-7 w-7 text-primary" /> Exam Structure
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
              Custom-build the assessment sequence for an academic year — unit tests, term exams, finals — per institution &amp; campus &amp; board. Drag tiles to reorder. Used by Curriculum to schedule chapters/topics.
            </p>
          </div>
          {canEdit && (
            <Button onClick={() => { setCreating(true); setSelectedId(null); }} data-testid="es-create-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New structure
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
          {/* Sidebar — list of structures */}
          <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="es-list">
            <div className="px-3 py-2.5 border-b border-border flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                Saved ({filteredStructures.length}/{structures.length})
              </span>
            </div>
            {/* Class filter — lets user narrow to a specific class or templates only */}
            {(classesQ.data || []).length > 0 && (
              <div className="px-3 py-2 border-b border-border bg-muted/30">
                <Select value={classFilter} onValueChange={setClassFilter}>
                  <SelectTrigger className="h-8 text-xs" data-testid="es-class-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[340px]">
                    <SelectItem value="all">All classes &amp; templates</SelectItem>
                    <SelectItem value="templates">Institution templates only</SelectItem>
                    <SelectGroup>
                      <SelectLabel className="text-[10px] uppercase tracking-wider">Per-class plans</SelectLabel>
                      {(classesQ.data || []).map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`es-class-filter-opt-${c.id}`}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
            {listQ.isLoading ? (
              <div className="p-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
            ) : filteredStructures.length === 0 ? (
              <div className="p-4 text-xs text-muted-foreground text-center">No structures match this filter.{canEdit && classFilter === "all" && " Create one above."}</div>
            ) : (
              <ul className="divide-y divide-border max-h-[60vh] overflow-y-auto">
                {filteredStructures.map((s) => (
                  <li
                    key={s.id}
                    onClick={() => { setSelectedId(s.id); setCreating(false); }}
                    className={`px-3 py-2.5 cursor-pointer hover:bg-muted/50 ${selectedId === s.id ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}
                    data-testid={`es-row-${s.id}`}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {s.class_id ? (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 border border-indigo-200" data-testid={`es-row-class-${s.id}`}>
                          {classMap[s.class_id] || "Class"}
                        </span>
                      ) : (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                          Template
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-semibold truncate">{s.name}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <FileText className="h-2.5 w-2.5" />
                      {s.nodes.length} assessments
                      {s.board && <span>· {s.board.toUpperCase()}</span>}
                      {s.academic_year && <span>· {s.academic_year}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Editor */}
          <div>
            {creating ? (
              <StructureEditor
                institutions={instQ.data || []}
                boards={boardsQ.data}
                classes={classesQ.data || []}
                initialClassId={classFilter !== "all" && classFilter !== "templates" ? classFilter : ""}
                onCancel={() => setCreating(false)}
                onSaved={(s) => { setCreating(false); setSelectedId(s.id); qc.invalidateQueries({ queryKey: ["es-list"] }); }}
              />
            ) : selected ? (
              <StructureEditor
                key={selected.id}
                existing={selected}
                institutions={instQ.data || []}
                boards={boardsQ.data}
                classes={classesQ.data || []}
                canEdit={canEdit}
                onSaved={() => qc.invalidateQueries({ queryKey: ["es-list"] })}
                onDeleted={() => { setSelectedId(null); qc.invalidateQueries({ queryKey: ["es-list"] }); }}
                onDuplicated={(s) => { setSelectedId(s.id); qc.invalidateQueries({ queryKey: ["es-list"] }); }}
              />
            ) : (
              <div className="border border-dashed border-border rounded-md bg-card p-16 text-center text-sm text-muted-foreground">
                <ClipboardCheck className="h-10 w-10 mx-auto mb-3 opacity-30" />
                Select a structure on the left, or click &ldquo;New structure&rdquo; to start one.
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function StructureEditor({ existing, institutions, boards, classes = [], initialClassId = "", canEdit = true, onSaved, onCancel, onDeleted, onDuplicated }) {
  const [institutionId, setInstitutionId] = useState(existing?.institution_id || "");
  const [campusId, setCampusId] = useState(existing?.campus_id || "");
  const [classId, setClassId] = useState(existing?.class_id || initialClassId || "");
  const [duplicating, setDuplicating] = useState(false);
  const [dupTargetClassId, setDupTargetClassId] = useState("");
  // Normalise legacy free-text board (e.g. "CBSE") to a known code when possible
  const normaliseBoard = (raw) => {
    if (!raw) return "";
    const flat = Object.values(boards?.groups || {}).flat();
    const m = flat.find(
      (b) => b.code.toLowerCase() === raw.toLowerCase() || b.name.toLowerCase().startsWith(raw.toLowerCase() + " ")
    );
    return m ? m.code : raw;
  };
  const [board, setBoard] = useState(normaliseBoard(existing?.board));
  // Re-normalise once boards have loaded (in case API was still loading on mount)
  useEffect(() => {
    if (existing?.board && boards?.groups) {
      const n = normaliseBoard(existing.board);
      if (n !== board) setBoard(n);
    }
  }, [boards, existing, board]);
  const [year, setYear] = useState(existing?.academic_year || "2025-26");
  const [name, setName] = useState(existing?.name || "");
  const [description, setDescription] = useState(existing?.description || "");
  // Ensure stable internal keys (for DND) and order
  const initNodes = (existing?.nodes || []).map((n, i) => ({
    ...n,
    _k: n.id || `tmp-${i}-${Math.random().toString(36).slice(2, 8)}`,
    order: i,
  }));
  const [nodes, setNodes] = useState(initNodes);
  const [editingNode, setEditingNode] = useState(null);
  const [busy, setBusy] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const campusQ = useQuery({
    queryKey: ["es-campuses", institutionId],
    queryFn: () => CampusesAPI.list({ institution_id: institutionId }),
    enabled: !!institutionId,
  });

  useEffect(() => {
    if (institutions.length && !institutionId) setInstitutionId(institutions[0].id);
  }, [institutions, institutionId]);

  const dirty = useMemo(() => {
    if (!existing) return true;
    const stripK = (arr) => arr.map(({ _k, ...n }, i) => ({ ...n, id: n.id || null, order: i }));
    return JSON.stringify({
      name, description, board, year, classId, nodes: stripK(nodes),
    }) !== JSON.stringify({
      name: existing.name, description: existing.description || "",
      board: existing.board || "", year: existing.academic_year || "",
      classId: existing.class_id || "",
      nodes: existing.nodes.map((n, i) => ({ ...n, id: n.id || null, order: i })),
    });
  }, [name, description, board, year, classId, nodes, existing]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setNodes((prev) => {
      const oldIdx = prev.findIndex((n) => n._k === active.id);
      const newIdx = prev.findIndex((n) => n._k === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx).map((n, i) => ({ ...n, order: i }));
    });
  };

  const addNode = () => {
    setEditingNode({ _k: `new-${Date.now()}`, id: null, type: "unit_test", name: "Unit Test 1", max_marks: 25, weight_pct: 5, scheduled_month: "", color: null, isNew: true });
  };

  const saveNode = (node) => {
    if (node.isNew) {
      const { isNew, ...rest } = node;
      setNodes((prev) => [...prev, { ...rest, _k: rest._k || `n-${Date.now()}`, id: undefined, order: prev.length }]);
    } else {
      setNodes((prev) => prev.map((n) => (n._k === node._k ? { ...n, ...node } : n)));
    }
    setEditingNode(null);
  };

  const deleteNode = (k) => {
    setNodes((prev) => prev.filter((n) => n._k !== k).map((n, i) => ({ ...n, order: i })));
  };

  const save = async () => {
    if (!institutionId || !name.trim()) { toast.error("Pick institution and give it a name"); return; }
    setBusy(true);
    try {
      const payload = {
        institution_id: institutionId,
        campus_id: campusId || null,
        class_id: classId || null,
        board: board.trim() || null,
        academic_year: year.trim() || null,
        name: name.trim(),
        description: description.trim() || null,
        nodes: nodes.map(({ _k, ...n }, i) => ({ ...n, order: i })),
      };
      const res = existing
        ? await ExamStructureAPI.update(existing.id, payload)
        : await ExamStructureAPI.create(payload);
      toast.success(`Structure ${existing ? "updated" : "created"}`);
      onSaved?.(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!existing || !window.confirm(`Delete structure "${existing.name}"?`)) return;
    try {
      await ExamStructureAPI.remove(existing.id);
      toast.success("Structure deleted");
      onDeleted?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed"); }
  };

  const duplicateToClass = async () => {
    if (!existing || !dupTargetClassId) { toast.error("Pick a class to copy to"); return; }
    const targetName = classes.find((c) => c.id === dupTargetClassId)?.name || "class";
    setBusy(true);
    try {
      const res = await ExamStructureAPI.create({
        institution_id: existing.institution_id,
        campus_id: existing.campus_id || null,
        class_id: dupTargetClassId,
        board: existing.board || null,
        academic_year: existing.academic_year || null,
        name: `${existing.name} — ${targetName}`,
        description: existing.description || null,
        nodes: existing.nodes.map((n, i) => ({ ...n, order: i })),
      });
      toast.success(`Copied to ${targetName}`);
      setDuplicating(false);
      setDupTargetClassId("");
      onDuplicated?.(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Duplicate failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="es-editor">
      {/* Meta form */}
      <div className="bg-card border border-border rounded-md p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Structure name <span className="text-rose-600">*</span></Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. AY 2025-26 — Senior CBSE Plan" disabled={!canEdit} data-testid="es-name" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Institution <span className="text-rose-600">*</span></Label>
          <Select value={institutionId} onValueChange={setInstitutionId} disabled={!!existing || !canEdit}>
            <SelectTrigger data-testid="es-institution"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>{institutions.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Campus (optional)</Label>
          <Select value={campusId || "all"} onValueChange={(v) => setCampusId(v === "all" ? "" : v)} disabled={!!existing || !canEdit}>
            <SelectTrigger data-testid="es-campus"><SelectValue placeholder="All campuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campuses</SelectItem>
              {(campusQ.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Board</Label>
          <Select value={board || "none"} onValueChange={(v) => setBoard(v === "none" ? "" : v)} disabled={!canEdit}>
            <SelectTrigger data-testid="es-board"><SelectValue placeholder="Select board…" /></SelectTrigger>
            <SelectContent className="max-h-[340px]">
              <SelectItem value="none">— None —</SelectItem>
              {board && !Object.values(boards?.groups || {}).flat().some((b) => b.code === board) && (
                <SelectItem value={board} data-testid={`es-board-opt-legacy`}>{board.toUpperCase()} (legacy)</SelectItem>
              )}
              {Object.entries(boards?.groups || {}).map(([group, items]) => (
                <SelectGroup key={group}>
                  <SelectLabel className="text-[10px] uppercase tracking-wider">{group}</SelectLabel>
                  {items.map((b) => (
                    <SelectItem key={b.code} value={b.code} data-testid={`es-board-opt-${b.code}`}>{b.name}</SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Academic year</Label>
          <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2025-26" disabled={!canEdit} data-testid="es-year" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">
            Class <span className="text-muted-foreground text-[10px] normal-case tracking-normal">(optional — leave blank for institution-wide template)</span>
          </Label>
          <Select value={classId || "none"} onValueChange={(v) => setClassId(v === "none" ? "" : v)} disabled={!canEdit}>
            <SelectTrigger data-testid="es-class"><SelectValue placeholder="Institution-wide template" /></SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="none">— Institution-wide template —</SelectItem>
              {classes
                .filter((c) => !campusId || c.campus_id === campusId || !c.campus_id)
                .map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`es-class-opt-${c.id}`}>{c.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Description</Label>
          <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} disabled={!canEdit} data-testid="es-description" />
        </div>
      </div>

      {/* Assessment sequence */}
      <div className="bg-card border border-border rounded-md p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-display font-bold text-base">Assessment sequence ({nodes.length})</h3>
            {nodes.length > 0 && canEdit && (
              <p className="text-[11px] text-muted-foreground mt-0.5">Drag tiles to reorder · hover to edit or change colour</p>
            )}
          </div>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={addNode} data-testid="es-add-node">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add assessment
            </Button>
          )}
        </div>
        {nodes.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No assessments yet. Click &ldquo;Add assessment&rdquo; to build the timeline.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={nodes.map((n) => n._k)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2.5" data-testid="es-flow">
                {nodes.map((n, idx) => (
                  <SortableNode
                    key={n._k}
                    node={n}
                    index={idx}
                    canEdit={canEdit}
                    onEdit={() => setEditingNode({ ...n, index: idx })}
                    onDelete={() => deleteNode(n._k)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Action row */}
      {canEdit && (
        <div className="flex items-center gap-2 justify-end flex-wrap">
          {existing && classes.length > 0 && (
            <div className="flex items-center gap-1.5" data-testid="es-duplicate-block">
              {duplicating ? (
                <>
                  <Select value={dupTargetClassId} onValueChange={setDupTargetClassId}>
                    <SelectTrigger className="w-[180px] h-9" data-testid="es-duplicate-class-select">
                      <SelectValue placeholder="Copy to class…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {classes.map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`es-duplicate-opt-${c.id}`}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={duplicateToClass} disabled={busy || !dupTargetClassId} data-testid="es-duplicate-confirm">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Copy"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setDuplicating(false); setDupTargetClassId(""); }}>Cancel</Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setDuplicating(true)} data-testid="es-duplicate-open">
                  <FileText className="h-3.5 w-3.5 mr-1.5" /> Copy to class…
                </Button>
              )}
            </div>
          )}
          {existing && (
            <Button variant="outline" onClick={remove} className="text-destructive" data-testid="es-delete">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          )}
          {onCancel && <Button variant="outline" onClick={onCancel}>Cancel</Button>}
          <Button onClick={save} disabled={busy || !dirty} data-testid="es-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-1.5" />}
            {existing ? "Save changes" : "Create structure"}
          </Button>
        </div>
      )}

      {editingNode && (
        <AssessmentNodeDialog
          node={editingNode}
          onClose={() => setEditingNode(null)}
          onSave={saveNode}
          testidPrefix="es"
        />
      )}
    </div>
  );
}

function SortableNode({ node, index, canEdit, onEdit, onDelete }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: node._k, disabled: !canEdit });

  const c = colorOf(node);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative ${c.bg} ${c.border} ${c.text} border-2 rounded-lg p-3 shadow-sm hover:shadow-md transition group ${isDragging ? "ring-2 ring-offset-2 " + c.ring : ""}`}
      data-testid={`es-node-${index}`}
    >
      {/* Order pill + drag handle */}
      <div className="flex items-center justify-between mb-1.5">
        <span className={`${c.pill} text-white text-[9px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center`}>
          {index + 1}
        </span>
        {canEdit && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing opacity-40 hover:opacity-100 touch-none"
            aria-label="Drag to reorder"
            data-testid={`es-drag-${index}`}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">
        {TYPE_LABELS[node.type] || node.type}
      </div>
      <div className="font-display font-bold text-sm leading-tight mt-0.5 line-clamp-2">{node.name}</div>
      <div className="text-[10px] mt-1.5 opacity-75 flex items-center gap-1.5 flex-wrap">
        {node.max_marks != null && <span>{node.max_marks} marks</span>}
        {node.weight_pct != null && <span>· {node.weight_pct}%</span>}
      </div>
      {node.scheduled_month && <div className="text-[10px] opacity-60 mt-0.5">{node.scheduled_month}</div>}

      {canEdit && (
        <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
          <button type="button" onClick={onEdit} className="h-5 w-5 rounded-full bg-white border border-border flex items-center justify-center shadow" data-testid={`es-edit-${index}`}>
            <PenLine className="h-2.5 w-2.5" />
          </button>
          <button type="button" onClick={onDelete} className="h-5 w-5 rounded-full bg-white border border-rose-200 flex items-center justify-center shadow" data-testid={`es-del-${index}`}>
            <Trash2 className="h-2.5 w-2.5 text-rose-600" />
          </button>
        </div>
      )}
    </div>
  );
}
